/**
 * AuthPromptSheet — lightweight auth prompt when unauthenticated user tries to save.
 *
 * TC-008 (work item 03): Tapping the save icon shows a bottom sheet with
 * "Log in" and "Create account" options. The search results remain visible beneath.
 * The URL does not change.
 *
 * For v1 this is a bottom sheet on all viewports.
 * Links carry the ?return= param so that after auth, the user lands back
 * on the page they were trying to use (work item 05-auth, TC-010).
 *
 * Accessibility: Radix Dialog (via BottomSheet primitive) provides:
 *   - Focus trap within the sheet when open
 *   - Escape key close
 *   - aria-modal="true" and role="dialog"
 *
 * v2 design changes (C4):
 *   - Heading copy changed to "Sign in to save schools." (shorter, action-oriented).
 *   - Heading drops from text-lg font-bold to text-base font-semibold.
 *   - Body copy updated to "Track programs with open roster spots — free."
 *   - Buttons stacked vertically (flex-col) instead of side-by-side — resolves
 *     narrow-screen truncation risk and gives full-width touch targets.
 *   - "Create account" downgraded to ghost button (border only) per button hierarchy.
 *   - "Not now" given min-h-[44px] touch target — previously a WCAG violation.
 *   - Deduplication: h2 heading removed from sheet body; BottomSheet title prop
 *     provides the accessible dialog name via aria-label on the Radix Dialog.Content.
 */

import React from 'react';
import { useLocation } from 'react-router-dom';
import { BottomSheet } from './ui/BottomSheet';

interface Props {
  isOpen: boolean;
  onClose: () => void;
}

export function AuthPromptSheet({ isOpen, onClose }: Props) {
  const location = useLocation();
  const returnParam = `?return=${encodeURIComponent(location.pathname + location.search)}`;

  return (
    <BottomSheet isOpen={isOpen} onClose={onClose} title="Sign in to save schools.">
      <div className="px-6 pb-6 pt-2">
        {/* C4: heading deduped — BottomSheet title prop is the accessible label.
            The visible heading here is the primary visual anchor inside the sheet. */}
        <p className="text-base font-semibold text-gray-900 mb-1">
          Sign in to save schools.
        </p>
        <p className="text-sm text-gray-500 mb-5">
          Track programs with open roster spots — free.
        </p>

        {/* C4: stacked vertical layout (was flex gap-3 horizontal) */}
        <div className="flex flex-col gap-2">
          {/* Primary CTA: filled green */}
          <a
            href={`/login${returnParam}`}
            className="w-full text-center bg-brand-primary text-white font-semibold py-2.5 min-h-[44px] flex items-center justify-center rounded-lg hover:bg-brand-primary-dk motion-safe:transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-primary focus-visible:ring-offset-2"
          >
            Log in
          </a>
          {/* Secondary CTA: ghost button with brand-primary border */}
          <a
            href={`/signup${returnParam}`}
            className="w-full text-center border border-brand-primary text-brand-primary font-semibold py-2.5 min-h-[44px] flex items-center justify-center rounded-lg hover:bg-brand-primary-lt motion-safe:transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-primary focus-visible:ring-offset-2"
          >
            Create account
          </a>
        </div>

        {/* C4: min-h-[44px] + flex centering — was a WCAG touch-target violation */}
        <button
          type="button"
          onClick={onClose}
          className="mt-3 w-full text-sm text-gray-400 hover:text-gray-600 min-h-[44px] flex items-center justify-center focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-primary focus-visible:ring-offset-2 rounded"
        >
          Not now
        </button>
      </div>
    </BottomSheet>
  );
}
